import apiClient from './client'

// Types
export interface AlertRule {
  id: number
  name: string
  description: string | null
  metric: string
  operator: string
  threshold: number
  duration_seconds: number
  severity: 'low' | 'medium' | 'high' | 'critical'
  is_active: boolean
  cooldown_minutes: number
  notify_google_chat: boolean
  notify_email: boolean
  notify_pc: boolean
  only_when_in_call: boolean
  created_at: string
  updated_at: string
}

export interface Alert {
  id: string  // UUID
  rule_id: number | null
  machine_id: string
  alert_type: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  message: string
  metric_value: number | null
  metadata: Record<string, unknown> | null
  triggered_at: string
  acknowledged_at: string | null
  resolved_at: string | null
  notes: string | null
  created_at: string
}

export interface CreateAlertRuleData {
  name: string
  description?: string
  metric: string
  operator: string
  threshold: number
  duration_seconds?: number
  severity: string
  cooldown_minutes?: number
  notify_google_chat?: boolean
  notify_email?: boolean
  notify_pc?: boolean
  only_when_in_call?: boolean
}

export interface UpdateAlertRuleData {
  name?: string
  description?: string
  metric?: string
  operator?: string
  threshold?: number
  duration_seconds?: number
  severity?: string
  is_active?: boolean
  cooldown_minutes?: number
  notify_google_chat?: boolean
  notify_email?: boolean
  notify_pc?: boolean
  only_when_in_call?: boolean
}

export interface AlertsSummary {
  total_unresolved: number
  by_severity: Record<string, number>
}

// Alert Rules API
export async function getAlertRules(isActive?: boolean): Promise<AlertRule[]> {
  const params = isActive !== undefined ? { is_active: isActive } : {}
  const response = await apiClient.get<AlertRule[]>('/alerts/rules', { params })
  return response.data
}

export async function getAlertRule(id: number): Promise<AlertRule> {
  const response = await apiClient.get<AlertRule>(`/alerts/rules/${id}`)
  return response.data
}

export async function createAlertRule(data: CreateAlertRuleData): Promise<AlertRule> {
  const response = await apiClient.post<AlertRule>('/alerts/rules', data)
  return response.data
}

export async function updateAlertRule(id: number, data: UpdateAlertRuleData): Promise<AlertRule> {
  const response = await apiClient.put<AlertRule>(`/alerts/rules/${id}`, data)
  return response.data
}

export async function deleteAlertRule(id: number): Promise<void> {
  await apiClient.delete(`/alerts/rules/${id}`)
}

export async function toggleAlertRule(id: number): Promise<{ id: number; is_active: boolean }> {
  const response = await apiClient.post<{ id: number; is_active: boolean }>(`/alerts/rules/${id}/toggle`)
  return response.data
}

// Alerts API
export async function getAlerts(params?: {
  machine_id?: string
  severity?: string
  resolved?: boolean
  skip?: number
  limit?: number
}): Promise<Alert[]> {
  const response = await apiClient.get<Alert[]>('/alerts/', { params })
  return response.data
}

export async function getUnresolvedAlerts(machineId?: string): Promise<Alert[]> {
  const params = machineId ? { machine_id: machineId } : {}
  const response = await apiClient.get<Alert[]>('/alerts/unresolved', { params })
  return response.data
}

export async function getAlertsSummary(): Promise<AlertsSummary> {
  const response = await apiClient.get<AlertsSummary>('/alerts/summary')
  return response.data
}

export async function acknowledgeAlert(id: string, notes?: string): Promise<{ alert_id: string; acknowledged_at: string; message: string }> {
  const response = await apiClient.post(`/alerts/${id}/acknowledge`, notes ? { notes } : {})
  return response.data
}

export async function resolveAlert(id: string, notes?: string): Promise<{ alert_id: string; resolved_at: string; message: string }> {
  const response = await apiClient.post(`/alerts/${id}/resolve`, notes ? { notes } : {})
  return response.data
}

export async function getRecentAlerts(machineId: string, limit?: number): Promise<Alert[]> {
  const params = limit ? { limit } : {}
  const response = await apiClient.get<Alert[]>(`/alerts/${machineId}/recent`, { params })
  return response.data
}

// Audit Logs
export interface AuditLog {
  id: number
  user_id: number | null
  user_email: string | null
  user_fullname: string | null
  action: string
  resource_type: string
  resource_id: string
  details: Record<string, unknown> | null
  ip_address: string | null
  created_at: string
}

export async function getAuditLogs(params?: {
  resource_type?: string
  resource_id?: string
  action?: string
  limit?: number
  offset?: number
}): Promise<AuditLog[]> {
  const response = await apiClient.get<AuditLog[]>('/alerts/audit-logs', { params })
  return response.data
}

export async function getAlertAuditHistory(alertId: string): Promise<AuditLog[]> {
  const response = await apiClient.get<AuditLog[]>(`/alerts/audit-logs/${alertId}/history`)
  return response.data
}
